import { useState, useCallback, useRef, useEffect } from "react";
import {
  displayInfoToast,
  displayErrorToast,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import { downloadFile } from "@src/helper/file.helper";
import { FilesApi } from "../files.api";

const MAXIMUM_RETRY = 6;
const RETRY_INTERVAL = 30_000 / MAXIMUM_RETRY;

type UseZipExportParams = {
  controleQuinquennalId: string | undefined;
  axeId: string | undefined;
  email?: string;
  onExported?: () => void;
};

export const useZipExport = ({
  controleQuinquennalId,
  axeId,
  email,
  onExported,
}: UseZipExportParams) => {
  const [isExporting, setIsExporting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isMountedRef = useRef(true);
  const onExportedRef = useRef(onExported);
  onExportedRef.current = onExported;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const retryAttempt = useCallback(
    async (idCq: string, idAxe: string, attempt = 0) => {
      if (!isMountedRef.current) return;
      if (attempt >= MAXIMUM_RETRY) {
        displayErrorToast(
          "Le traitement est plus long que prévu, nous vous enverrons un mail dès que les documents seront prêts."
        );
        setIsExporting(false);
        return;
      }

      const url = await FilesApi.getZipSignedUrl(idCq, idAxe);

      if (!isMountedRef.current) return;

      if (url) {
        displaySucessToast("L'export est prêt, vous pouvez le télécharger");
        downloadFile(url);
        setIsExporting(false);
        onExportedRef.current?.();
        return;
      }

      timeoutRef.current = setTimeout(() => {
        void retryAttempt(idCq, idAxe, attempt + 1);
      }, RETRY_INTERVAL);
    },
    []
  );

  const triggerExport = useCallback(async () => {
    if (!controleQuinquennalId || !axeId) return;
    if (isExporting) return;

    setIsExporting(true);

    displayInfoToast(
      "Les documents sont en cours de préparation. Merci de patienter.",
      { hideAfter: 10_000 }
    );

    try {
      await FilesApi.triggerZipExport(controleQuinquennalId, axeId, email);
      void retryAttempt(controleQuinquennalId, axeId);
    } catch {
      displayErrorToast(
        "Une erreur est survenue, merci de réessayer plus tard.",
        { hideAfter: 10_000 }
      );
      setIsExporting(false);
    }
  }, [controleQuinquennalId, axeId, email, isExporting, retryAttempt]);

  return {
    triggerExport,
    isExporting,
  };
};
